import React, { useState } from 'react';
import { Box, Typography, IconButton, Dialog, DialogTitle, Divider, Avatar, SnackbarOrigin } from '@mui/material';
import { useUpdateProfileMutation } from '../api/profileApi';
import ProfileForm from './ProfileForm';
import { Email, GitHub, LinkedIn, Edit } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import AutohideSnackbar from './SnackBar';

interface ProfileProps {
  isLoggedIn: boolean;
  profile: ProfileTypes;
}

interface ProfileTypes {
  id?: number;
  name: string;
  jobTitle: string;
  techStack: string[];  // Make sure this is an array of strings
  linkedin: string;
  github: string;
  email: string;
  profilePic?: string;
}

interface State extends SnackbarOrigin {
  open: boolean
}

const Profile: React.FC<ProfileProps> = ({ isLoggedIn, profile }) => {
  // const { data: profile } = useGetProfileQuery();
  const [updateProfile, response] = useUpdateProfileMutation();
  const { t } = useTranslation();
  const [editMode, setEditMode] = useState(false);
  const [homeProfileOpen, setProfileFormOpen] = useState(false);
  const [file, setFile] = useState(null)
  const [state, setState] = React.useState<State>({
		open: false,
		vertical: 'bottom',
		horizontal: 'center',
	});

  const handleProfileFormOpen = () => {
    setProfileFormOpen(true)
    setEditMode(true)
  }

  const handleProfileFormClose = () => {
    setProfileFormOpen(false)
    setEditMode(false)
  }

  const handleSave = async (updatedProfile: Partial<ProfileTypes | undefined>) => {
    if (profile) {
      await updateProfile({ id: profile?.id, ...updatedProfile });
      setEditMode(false);
      setProfileFormOpen(false)
      setState((newState) => ({
        ...newState, 
        open: true,
        vertical: 'bottom',
        horizontal: 'center',
      }))
    }
  };

  // if (isLoading) return <CircularProgress />;
  // if (error) return <Typography variant="h6">Error loading profile data</Typography>;

  return (
    <Box sx={{ position: 'relative', display: 'inline-block' }}>
      <AutohideSnackbar
        message={
          response?.isSuccess
          ? 'Profile updated successfully' 
          : 'Profile update failed'
        }
        severity={response.isSuccess ? 'success' : 'error'}
        state={state} 
				setState={setState}
				vertical={state.vertical}
				horizontal={state.horizontal}
      />
        {/* Circular Profile Picture */}
        <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
          <Avatar
            alt="Profile Picture"
            // 
            src="https://res.cloudinary.com/dx8jbju8t/image/upload/v1745159424/profile_rd4xtp.jpg"
            
            sx={{
              width: { xs: 150, sm: 200 }, // Responsive width
              height: { xs: 150, sm: 200 },
              mb: 4, // margin bottom
              border: '3px solid lightgrey',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          />

        <Box
          sx={{
            color: '#ffffff',
            transform: 'translateX(-100%)', // Initially offscreen
            animation: 'slideIn 1.5s forwards', // Slide in animation
            animationDelay: '0.5s', // Delay before the animation starts
            opacity: 0, // Initially hidden
            '@keyframes slideIn': {
              '0%': {
                transform: 'translateX(-100%)',
                opacity: 0,
              },
              '100%': {
                transform: 'translateX(0)',
                opacity: 1,
              },
            },
          }}
        >
          {isLoggedIn && (
            <IconButton
              onClick={handleProfileFormOpen}
              sx={{
                position: 'absolute',
                top: -5,
                right: 40,
                color: 'white',
                backgroundColor: '#3c4043',
              }}
            >
              <Edit />
            </IconButton>
          )}
          {Array.isArray(profile) && profile.map((item: ProfileTypes) => (
            <Box key={item.id}>
              <Typography variant="h5">{item.name}</Typography>
              <Typography variant="h4">{t(`jobTitle.${item.jobTitle}`, item.jobTitle)}</Typography>
              <Typography variant="body1">{item?.techStack?.map((val) => val).join(" | ")}</Typography>
              {/* Social Media Icons (LinkedIn, GitHub, Email) */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  mt: 4, // Margin top for spacing
                  flexWrap: 'wrap', // Allow icons to wrap on smaller screens
                }}
              >
                {/* LinkedIn Icon */}
                <IconButton
                  color="primary"
                  aria-labelledby="LinkedIn"
                  href={item.linkedin}
                  target="_blank"
                  sx={{ mx: 1 }} // Margin between icons
                >
                  <LinkedIn sx={{ fontSize: 40, color: 'white' }} />
                </IconButton>

                {/* GitHub Icon */}
                <IconButton
                  color="primary"
                  aria-labelledby="GitHub"
                  href={item.github}
                  target="_blank"
                  sx={{ mx: 1 }}
                >
                  <GitHub sx={{ fontSize: 40, color: 'white' }} />
                </IconButton>

                {/* Email Icon */}
                <IconButton
                  color="primary"
                  aria-labelledby="Email"
                  href={`mailto:${item.email}`}
                  sx={{ mx: 1 }}
                >
                  <Email sx={{ fontSize: 40, color: 'white' }} />
                </IconButton>
              </Box>

              <Dialog open={homeProfileOpen} onClose={handleProfileFormClose} >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    paddingBottom: 0,
                  }}
                >
                  <DialogTitle>{t('Edit Profile')}</DialogTitle>
                  <IconButton onClick={handleProfileFormClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
                <Divider variant='middle' />
                <ProfileForm
                  file={file}
                  setFile={setFile}
                  editMode={editMode}
                  initialProfile ={profile}
                  onUpdate={handleSave}
                  onCancel={() => setProfileFormOpen(false)}
                />
              </Dialog>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
    
  );
};

export default Profile;
